import { FileAccept, PreviewableFile } from "../types/files";
import {
  AudioFileAccept,
  CsvFileAccept,
  ImageFileAccept,
  PdfFileAccept,
  StrictImageFileAccept,
  TextFileAccept,
  VideoFileAccept,
} from "../utils/core";

// Derive extension/content-type lists straight from the accept maps in types/hooks.ts so
// every file type DefaultFileAccept allows to be uploaded is accounted for here too.
const extensionsOf = (accept: FileAccept) =>
  Object.values(accept)
    .flat()
    .map((ext) => ext.replace(".", "").toLowerCase());

const contentTypesOf = (accept: FileAccept) =>
  Object.keys(accept).map((type) => type.toLowerCase());

const IMAGE_EXTENSIONS = [
  ...extensionsOf(ImageFileAccept),
  ...extensionsOf(StrictImageFileAccept),
];
const IMAGE_CONTENT_TYPES = [
  ...contentTypesOf(ImageFileAccept),
  ...contentTypesOf(StrictImageFileAccept),
];
const PDF_EXTENSIONS = extensionsOf(PdfFileAccept);
const PDF_CONTENT_TYPES = contentTypesOf(PdfFileAccept);
const TEXT_EXTENSIONS = extensionsOf(TextFileAccept);
const TEXT_CONTENT_TYPES = contentTypesOf(TextFileAccept);
// text/csv is one of the few mime types browsers force-download rather than render (same as
// navigating straight to a .csv URL) — an <iframe src> to it downloads instead of previewing,
// so csv gets its own "fetch as text" path instead of the iframe path used for pdf/text.
const CSV_EXTENSIONS = extensionsOf(CsvFileAccept);
const CSV_CONTENT_TYPES = contentTypesOf(CsvFileAccept);
const AUDIO_EXTENSIONS = extensionsOf(AudioFileAccept);
const AUDIO_CONTENT_TYPES = contentTypesOf(AudioFileAccept);
const VIDEO_EXTENSIONS = extensionsOf(VideoFileAccept);
const VIDEO_CONTENT_TYPES = contentTypesOf(VideoFileAccept);

export type PreviewKind =
  | "image"
  | "pdf"
  | "text"
  | "csv"
  | "audio"
  | "video"
  | "unsupported";

// Files can arrive either as a freshly picked UploadedFile (preview blob url, File.type)
// or a server-returned attachment (url, contentType) — normalize both before rendering.
export function getPreviewMeta(file: PreviewableFile | null | undefined) {
  const name = file?.filename || file?.name || "";
  const src = file?.url || file?.preview || "";
  const contentType = (file?.contentType || file?.type || "").toLowerCase();
  const extension = name.split(".").pop()?.toLowerCase() || "";

  const matches = (extensions: string[], contentTypes: string[]) =>
    extensions.includes(extension) ||
    contentTypes.includes(contentType) ||
    // covers wildcard entries like "image/*"
    contentTypes.some(
      (type) =>
        type.endsWith("/*") && contentType.startsWith(type.slice(0, -1)),
    );

  let kind: PreviewKind = "unsupported";
  if (matches(IMAGE_EXTENSIONS, IMAGE_CONTENT_TYPES)) kind = "image";
  else if (matches(PDF_EXTENSIONS, PDF_CONTENT_TYPES)) kind = "pdf";
  else if (matches(CSV_EXTENSIONS, CSV_CONTENT_TYPES)) kind = "csv";
  else if (matches(TEXT_EXTENSIONS, TEXT_CONTENT_TYPES)) kind = "text";
  else if (matches(AUDIO_EXTENSIONS, AUDIO_CONTENT_TYPES)) kind = "audio";
  else if (matches(VIDEO_EXTENSIONS, VIDEO_CONTENT_TYPES)) kind = "video";

  return { name, src, kind };
}
